import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function mapsKey() {
  const htmlds = document.documentElement.dataset;
  return htmlds.mapsKey || "";
}

function cancelledError() {
  const err = new Error("geocoder request cancelled");
  err.cancelled = true;
  return err;
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function validLatLng(lat, lng) {
  return isFiniteNumber(lat) && isFiniteNumber(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.autocompleteRequest = null;
    this.autocompleteSeq = 0;
    this.reverseRequest = null;
    this.reverseSeq = 0;
  }

  static isCancelled(err) {
    return !!(err && err.cancelled);
  }

  cancelAutocomplete() {
    this.autocompleteSeq++;
    if (this.autocompleteRequest != null) {
      this.autocompleteRequest.cancel();
      this.autocompleteRequest = null;
    }
  }

  cancelReverse() {
    this.reverseSeq++;
    if (this.reverseRequest != null) {
      this.reverseRequest.cancel();
      this.reverseRequest = null;
    }
  }

  cancelAll() {
    this.cancelAutocomplete();
    this.cancelReverse();
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!validLatLng(lat, lng)) {
      return null;
    }

    const props = l.properties;
    const val = (k) => (typeof props[k] === "string" || typeof props[k] === "number") && props[k] !== "" ? props[k] : null;
    const name = val("name");
    const geoaddress = {};

    let type = val("type");
    if (!type) {
      // Legacy-Fallback ohne properties.type
      if (props.osm_key == "highway") {
        type = "street";
      } else if (props.osm_key == "boundary" || (props.osm_key == "place" && props.osm_value == "suburb")) {
        type = "district";
      } else if (props.osm_key == "place") {
        type = "city";
      } else if (val("housenumber")) {
        type = "house";
      }
    }

    if (type == "house") {
      geoaddress.street = val("street");
      geoaddress.number = val("housenumber");
      geoaddress.city = val("city");
      geoaddress.district = val("district");
    } else if (type == "street") {
      geoaddress.street = val("street") || name;
      geoaddress.city = val("city");
      geoaddress.district = val("district");
    } else if (type == "district") {
      geoaddress.city = val("city");
      geoaddress.district = val("district") || name;
      if (geoaddress.district && geoaddress.district == geoaddress.city) {
        geoaddress.district = null;
      }
    } else if (type == "city") {
      geoaddress.city = val("city") || name;
    } else {
      // locality, county, state, country, other
      geoaddress.city = val("city");
      geoaddress.district = val("district");
    }

    geoaddress.zip = val("postcode");
    geoaddress.lat = lat;
    geoaddress.lng = lng;
    geoaddress.state = val("state");
    geoaddress.country = val("country");

    Object.keys(geoaddress).forEach(k => {
      if (geoaddress[k] === null) {
        delete geoaddress[k];
      }
    });

    let text = this._geoaddressText(geoaddress);
    if (name && (type == "house" || type == "locality" || !text) && text.indexOf(name) !== 0) {
      text = text ? name + ", " + text : name;
    }
    geoaddress.text = text;

    return geoaddress;
  }

  _request(url, params, slot) {
    const source = this.axios.CancelToken.source();
    let timer = null;
    let settled = false;

    const promise = new Promise((resolve, reject) => {
      const finish = (fn, arg) => {
        if (settled) {
          return;
        }
        settled = true;
        if (timer != null) {
          clearTimeout(timer);
        }
        if (this[slot] && this[slot].source === source) {
          this[slot] = null;
        }
        fn(arg);
      };

      timer = setTimeout(() => {
        source.cancel();
        finish(reject, new Error("geocoder timeout"));
      }, REQUEST_TIMEOUT);

      this[slot] = {
        source,
        cancel: () => {
          source.cancel();
          finish(reject, cancelledError());
        }
      };

      this.axios.get(url, {
        cancelToken: source.token,
        timeout: REQUEST_TIMEOUT,
        params
      }).then(res => {
        finish(resolve, res.data);
      }).catch(err => {
        finish(reject, this.axios.isCancel(err) ? cancelledError() : err);
      });
    });

    return promise;
  }

  geocode(params) {
    this.cancelAutocomplete();
    const seq = this.autocompleteSeq;

    return this._request(SEARCH_URL, params, "autocompleteRequest").then(data => {
      if (seq !== this.autocompleteSeq) {
        throw cancelledError();
      }

      const results = [];
      if (data && Array.isArray(data.features)) {
        data.features.forEach(l => {
          const geoaddress = this._geoaddress(l);
          if (geoaddress) {
            results.push(geoaddress);
          }
        });
      }

      return results;
    }, err => {
      if (!Geocoder.isCancelled(err) && seq === this.autocompleteSeq) {
        console.warn("Geocoder:", err && err.message ? err.message : err);
      }
      throw Geocoder.isCancelled(err) || seq !== this.autocompleteSeq ? cancelledError() : err;
    });
  }

  autocomplete(q) {
    const key = mapsKey();
    q = typeof q === "string" ? q.trim() : "";

    if (!q || !key) {
      this.cancelAutocomplete();
      if (!key) {
        console.warn("Geocoder: kein Maps-Key verfügbar");
      }
      return Promise.resolve([]);
    }

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return this.geocode(params);
  }

  // Liefert die Adresse oder null (kein Treffer, Fehler, kein Key, veraltete Anfrage).
  reverse(lat, lng) {
    lat = Number(lat);
    lng = Number(lng);
    const key = mapsKey();

    if (!validLatLng(lat, lng) || !key) {
      if (!key) {
        console.warn("Geocoder: kein Maps-Key verfügbar");
      }
      return Promise.resolve(null);
    }

    this.cancelReverse();
    const seq = this.reverseSeq;

    const params = new URLSearchParams();
    params.append("lat", String(lat));
    params.append("lon", String(lng));
    params.append("lang", "de");
    params.append("key", key);

    return this._request(REVERSE_URL, params, "reverseRequest").then(data => {
      if (seq !== this.reverseSeq) {
        return null;
      }
      if (data && Array.isArray(data.features) && data.features.length > 0) {
        return this._geoaddress(data.features[0]);
      }
      return null;
    }, err => {
      if (!Geocoder.isCancelled(err) && seq === this.reverseSeq) {
        console.warn("Geocoder:", err && err.message ? err.message : err);
      }
      return null;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory, Geocoder };

export default {};
